import type { ReactNode } from "react";

import { Navigate } from "@tanstack/react-router";

import { getOnBehalfOf } from "@/lib/api/on-behalf";
import { useAuth } from "@/lib/auth/auth-context";
import { isExpert, isPartner, isSuperUser } from "@/lib/auth/roles";

/**
 * Access gate mirroring the old app's `SubscriptionRequiredMixin`: a user who
 * may not reach the Activateur is **redirected**, not shown an inline notice
 * (finding C-1). Super users and partners go to their group page (they
 * self-allocate a licence from the pool there); everyone else goes to billing.
 *
 * `has_activateur_access` is the backend-computed flag the server gate itself
 * asks — licence, paid subscription, Django superuser, *or* a régional/local
 * partner who holds no licence (OAC-462). Reading `has_active_subscription`
 * here instead would redirect that partner away from an application the Django
 * app serves them.
 *
 * Partner on-behalf mode is exempt: the partner reaches the member's
 * questionnaire through consent, not their own subscription (the old app's
 * on-behalf views don't extend SubscriptionRequiredMixin), so we never redirect
 * while an on-behalf target is active.
 */
export function RequireSubscription({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  if (user && !user.has_activateur_access && !getOnBehalfOf()) {
    // Experts have no enterprise/subscription — their home is the expert
    // dashboard, not billing.
    const to = isExpert(user)
      ? "/experts/dashboard"
      : isSuperUser(user) || isPartner(user)
        ? "/partner-group"
        : "/billing";
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
}
